import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Block, CoachButton, Eyebrow, Field, inputClass } from '../primitives';
import { coaching, type Goal } from '../../lib/api';
import { focusLabel, longDay } from '../../lib/format';
import { useEntitlement, useRefreshCoaching } from '../../lib/queries';
import { ProLock } from '../pro-lock';
import { isGoalLimit } from '../../lib/pro';

/**
 * Active goals with progress and an inline new-goal form. `hub` puts a header row
 * with the add button above the list (desktop Achieve); `page` keeps the add
 * button under the list (the My goals page).
 */
export function GoalsPanel({ goals, variant }: { goals: Goal[]; variant: 'page' | 'hub' }) {
  const refresh = useRefreshCoaching();
  const navigate = useNavigate();
  const entitlement = useEntitlement();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [blocked, setBlocked] = useState(false);
  const create = useMutation({
    mutationFn: () => coaching.createGoal({ title: title.trim() }),
    meta: { successMessage: 'Goal added.' },
    onSuccess: (goal) => {
      setTitle('');
      setAdding(false);
      void refresh();
      navigate(`/achieve/goals/${goal.id}`);
    },
    // The server has the last word on the limit; this catches the case where
    // another tab used the last slot.
    onError: (err) => setBlocked(isGoalLimit(err)),
  });

  // A free member's cap. Known before they type, so the form is replaced by
  // the reason rather than taking a goal and refusing it.
  const cap = entitlement.data?.limits.activeGoals ?? null;
  const atLimit = blocked || (cap !== null && goals.length >= cap);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (title.trim().length >= 3) create.mutate();
  };

  const form = (
    <form onSubmit={onSubmit} className="space-y-4">
      <Field label="New goal" htmlFor="new-goal">
        <input
          id="new-goal"
          className={inputClass}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          autoFocus
        />
      </Field>
      <div className="flex gap-3">
        <CoachButton type="submit" disabled={title.trim().length < 3 || create.isPending}>
          Add goal
        </CoachButton>
        <CoachButton kind="quiet" onClick={() => setAdding(false)}>
          Cancel
        </CoachButton>
      </div>
    </form>
  );

  // At the cap, the reason takes the form's place: the list is still theirs to
  // read, and "Add another goal" is still where they expect it.
  const formOrLock = atLimit ? <ProLock feature="goal_limit" /> : form;

  const list =
    goals.length === 0 ? (
      <Block
        title="Your next chapter starts with a clear goal."
        action={
          adding ? undefined : (
            <CoachButton onClick={() => setAdding(true)}>Create my first goal</CoachButton>
          )
        }
      >
        Tell us what you want to achieve and we'll help you turn it into daily action.
      </Block>
    ) : (
      goals.map((g) => (
        <Link
          key={g.id}
          to={`/achieve/goals/${g.id}`}
          className="block border-b border-border px-5 py-5 transition-colors hover:bg-surface"
        >
          <Eyebrow>{g.isPrimary ? 'Primary goal' : (focusLabel(g.category) ?? 'Goal')}</Eyebrow>
          <p className="mt-1.5 font-display text-xl leading-snug text-ink">{g.title}</p>
          <div className="mt-3 flex items-center gap-3">
            <div className="h-px flex-1 bg-[rgba(16,32,26,0.14)]">
              <div className="h-px bg-verde" style={{ width: `${g.progressPercent}%` }} />
            </div>
            <span className="font-mono text-xs text-ink">{g.progressPercent}%</span>
          </div>
          {g.targetDate && <p className="eyebrow mt-2">Target {longDay(g.targetDate)}</p>}
        </Link>
      ))
    );

  if (variant === 'hub') {
    return (
      <>
        <div className="flex items-center justify-between gap-4 border-b border-border px-5 pb-3 pt-6">
          <Eyebrow>My goals · {goals.length} active</Eyebrow>
          {goals.length > 0 && !adding && (
            <CoachButton onClick={() => setAdding(true)}>Add goal</CoachButton>
          )}
        </div>
        {adding &&
          (atLimit ? formOrLock : <div className="border-b border-border px-5 py-6">{form}</div>)}
        {list}
      </>
    );
  }

  return (
    <>
      {list}
      {adding && atLimit && formOrLock}
      <section className="px-5 py-6">
        {adding
          ? !atLimit && form
          : goals.length > 0 && (
              <CoachButton onClick={() => setAdding(true)}>Add another goal</CoachButton>
            )}
      </section>
    </>
  );
}
